import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, MessageCircle, Clock, MapPin, Phone, Send, Users, Headphones } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';
import { COMPANY } from '../lib/company';

const ContactPage = () => {
  const { t } = useTranslation();
  useSEO('contact');

  const contactMethods = [
    {
      id: 'support',
      icon: Headphones,
      title: t('contact.methods.support.title'),
      subtitle: t('contact.methods.support.subtitle'),
      contact: 'support@ip9.io',
      description: t('contact.methods.support.description'),
      color: 'from-blue-500/10 to-cyan-500/10',
      iconColor: 'text-blue-400',
      responseTime: t('contact.methods.support.responseTime')
    },
    {
      id: 'sales',
      icon: Users,
      title: t('contact.methods.sales.title'),
      subtitle: t('contact.methods.sales.subtitle'),
      contact: 'sales@ip9.io',
      description: t('contact.methods.sales.description'),
      color: 'from-green-500/10 to-emerald-500/10',
      iconColor: 'text-green-400',
      responseTime: t('contact.methods.sales.responseTime')
    },
    {
      id: 'telegram',
      icon: MessageCircle,
      title: t('contact.methods.telegram.title'),
      subtitle: t('contact.methods.telegram.subtitle'),
      contact: '@ip9solutions',
      description: t('contact.methods.telegram.description'),
      color: 'from-purple-500/10 to-violet-500/10',
      iconColor: 'text-purple-400',
      responseTime: t('contact.methods.telegram.responseTime')
    }
  ];

  const businessHours = [
    {
      day: t('contact.businessHours.weekdays'),
      hours: '9:00 AM - 6:00 PM PST'
    },
    {
      day: t('contact.businessHours.weekends'),
      hours: '10:00 AM - 4:00 PM PST'
    }
  ];

  const supportCategories = [
    {
      category: t('contact.supportCategories.technical.title'),
      items: t('contact.supportCategories.technical.items')
    },
    {
      category: t('contact.supportCategories.billing.title'),
      items: t('contact.supportCategories.billing.items')
    },
    {
      category: t('contact.supportCategories.sales.title'),
      items: t('contact.supportCategories.sales.items')
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-6">
                <Mail className="w-12 h-12 text-cyan-400" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('contact.title')}
              </h1>
              <p className="text-xl text-slate-400 mb-4">
                {t('contact.subtitle')}
              </p>
              <p className="text-lg text-slate-300">
                {t('contact.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('contact.methods.title')}
              </h2>
              <p className="text-lg text-slate-400">
                {t('contact.methods.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactMethods.map((method) => {
                const IconComponent = method.icon;
                return (
                  <div key={method.id} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-slate-700 transition-colors">
                    <div className={`flex items-center justify-center w-12 h-12 bg-gradient-to-br ${method.color} rounded-xl mb-6`}>
                      <IconComponent className={`w-6 h-6 ${method.iconColor}`} />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-2">
                      {method.title}
                    </h3>
                    <p className="text-slate-400 mb-4">
                      {method.subtitle}
                    </p>
                    
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        {method.id === 'telegram' ? (
                          <MessageCircle className="w-4 h-4 text-purple-400" />
                        ) : (
                          <Mail className="w-4 h-4 text-cyan-400" />
                        )}
                        <span className="text-white font-mono text-sm">
                          {method.contact}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-400 text-sm">
                          {method.responseTime}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-slate-300 text-sm">
                      {method.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Business Hours & Location */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Business Hours */}
              <div className="bg-slate-950/50 border border-slate-800 rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl mr-4">
                    <Clock className="w-5 h-5 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    {t('contact.businessHours.title')}
                  </h3>
                </div>
                
                <div className="space-y-4">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-slate-800 last:border-b-0">
                      <span className="text-slate-300">{schedule.day}</span>
                      <span className="text-white font-mono text-sm">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                  <p className="text-cyan-300 text-sm">
                    {t('contact.businessHours.note')}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="bg-slate-950/50 border border-slate-800 rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl mr-4">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    {t('contact.location.title')}
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-white font-semibold mb-2">{COMPANY.legalName}</h4>
                    <p className="text-slate-300 leading-relaxed">
                      {COMPANY.addressLines.map((line) => (
                        <React.Fragment key={line}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                  <p className="font-mono text-xs text-slate-500">
                    {COMPANY.jurisdiction} · No. {COMPANY.companyNumber}
                  </p>
                  <div>
                    <h4 className="text-white font-semibold mb-2">{t('contact.location.coverage')}</h4>
                    <p className="text-slate-300">{t('contact.location.coverageDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Support Categories */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('contact.supportCategories.title')}
              </h2>
              <p className="text-lg text-slate-400">
                {t('contact.supportCategories.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {supportCategories.map((category, index) => (
                <div key={index} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    {category.category}
                  </h3>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-slate-300 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emergency Contact */}
        <section className="py-12 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-8 text-center">
              <Phone className="w-8 h-8 text-amber-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                {t('contact.emergency.title')}
              </h3>
              <p className="text-slate-300 mb-4">
                {t('contact.emergency.description')}
              </p>
              <div className="inline-flex items-center space-x-2 bg-slate-950/50 rounded-lg px-4 py-2">
                <Mail className="w-4 h-4 text-amber-400" />
                <span className="text-white font-mono">urgent@ip9.io</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;